import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Headphones, Ticket, Loader2, X, ArrowRight, TrendingUp, Sparkles } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export function GlobalSearch() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [trending, setTrending] = useState(null);
    const [showTrending, setShowTrending] = useState(false);
    const navigate = useNavigate();
    const ref = useRef(null);
    const debounceRef = useRef(null);

    // Fetch trending on mount
    const fetchTrending = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/public/trending`);
            if (res.ok) setTrending(await res.json());
        } catch {}
    }, []);

    useEffect(() => { fetchTrending(); }, [fetchTrending]);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowTrending(false); } };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (query.length < 2) { setResults(null); setOpen(false); if (query.length === 0 && trending) setShowTrending(true); return; }
        setShowTrending(false);
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`${API}/api/public/search?q=${encodeURIComponent(query)}`);
                if (res.ok) {
                    const data = await res.json();
                    setResults(data);
                    setOpen(true);
                }
            } catch {}
            finally { setLoading(false); }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    const go = (path) => { navigate(path); setOpen(false); setShowTrending(false); setQuery(''); };

    const applyTerm = (term) => { setQuery(term); setShowTrending(false); };

    const totalResults = results ? (results.cities?.length || 0) + (results.attractions?.length || 0) + (results.listings?.length || 0) : 0;

    const getImageUrl = (img) => {
        if (!img) return null;
        if (img.startsWith('http')) return img;
        return `${API}${img}`;
    };

    return (
        <div className="relative w-full max-w-2xl mx-auto" ref={ref}>
            {/* Search Input */}
            <div className="relative group">
                <Search className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-gold transition-colors" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                        if (query && results && totalResults > 0) setOpen(true);
                        else if (!query && trending) setShowTrending(true);
                    }}
                    placeholder="Search cities, attractions, experiences..."
                    className="w-full h-12 sm:h-14 pl-12 sm:pl-14 pr-12 rounded-2xl bg-white/[0.07] border border-white/10 text-white placeholder:text-white/30 text-sm sm:text-base outline-none focus:border-gold/40 focus:bg-white/[0.09] focus:ring-1 focus:ring-gold/20 transition-all backdrop-blur-sm"
                    data-testid="global-search-input"
                />
                {loading && <Loader2 className="absolute right-14 top-1/2 -translate-y-1/2 w-4 h-4 text-gold animate-spin" />}
                {query && (
                    <button onClick={() => { setQuery(''); setResults(null); setOpen(false); setShowTrending(true); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 transition-colors" data-testid="search-clear">
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                )}
            </div>

            {/* Results Dropdown */}
            {open && results && (
                <div className="absolute top-full mt-2 w-full bg-[#121620] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="search-results">
                    {totalResults === 0 ? (
                        <div className="py-8 text-center">
                            <Search className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No results for "{query}"</p>
                        </div>
                    ) : (
                        <div className="max-h-[420px] overflow-y-auto">
                            {/* Cities */}
                            {results.cities?.length > 0 && (
                                <div>
                                    <div className="px-4 py-2 bg-white/[0.02] border-b border-white/5">
                                        <p className="text-[10px] text-gold uppercase tracking-widest font-semibold">Cities</p>
                                    </div>
                                    {results.cities.map(city => (
                                        <button key={city.id} onClick={() => go(`/city/${city.name.toLowerCase().replace(/\s+/g, '-')}`)}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5" data-testid={`search-city-${city.id}`}>
                                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                                {city.image_url ? (
                                                    <img src={city.image_url} alt={city.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center"><MapPin className="w-4 h-4 text-gold/40" /></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-white truncate">{city.name}</p>
                                                <p className="text-xs text-muted-foreground">{city.country}</p>
                                            </div>
                                            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Listings / Experiences */}
                            {results.listings?.length > 0 && (
                                <div>
                                    <div className="px-4 py-2 bg-white/[0.02] border-b border-white/5">
                                        <p className="text-[10px] text-amber-400 uppercase tracking-widest font-semibold">Experiences</p>
                                    </div>
                                    {results.listings.map(listing => (
                                        <button key={listing.id} onClick={() => go(`/experience/${listing.id}`)}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5" data-testid={`search-listing-${listing.id}`}>
                                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                                {listing.featured_image ? (
                                                    <img src={getImageUrl(listing.featured_image)} alt={listing.title} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center"><Ticket className="w-4 h-4 text-amber-400/40" /></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-white truncate">{listing.title}</p>
                                                <div className="flex items-center gap-2">
                                                    {listing.category && <span className="text-xs text-muted-foreground">{listing.category}</span>}
                                                    {listing.from_price > 0 && <span className="text-xs text-gold font-medium">from {formatCurrency(listing.from_price, listing.currency?.toUpperCase() || 'EUR')}</span>}
                                                </div>
                                            </div>
                                            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Attractions */}
                            {results.attractions?.length > 0 && (
                                <div>
                                    <div className="px-4 py-2 bg-white/[0.02] border-b border-white/5">
                                        <p className="text-[10px] text-emerald-400 uppercase tracking-widest font-semibold">Attractions</p>
                                    </div>
                                    {results.attractions.map(attr => (
                                        <button key={attr.id} onClick={() => go(`/city/${attr.city_name?.toLowerCase().replace(/\s+/g, '-') || 'explore'}`)}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5" data-testid={`search-attraction-${attr.id}`}>
                                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                                {attr.featured_image ? (
                                                    <img src={getImageUrl(attr.featured_image)} alt={attr.title} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center"><Headphones className="w-4 h-4 text-emerald-400/40" /></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-white truncate">{attr.title}</p>
                                                <div className="flex items-center gap-2">
                                                    {attr.category && <span className="text-xs text-muted-foreground">{attr.category}</span>}
                                                    {attr.city_name && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {attr.city_name}</span>}
                                                </div>
                                            </div>
                                            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Trending Suggestions */}
            {showTrending && !open && trending && (
                <div className="absolute top-full mt-2 w-full bg-[#121620]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="trending-suggestions">
                    <div className="p-4">
                        {/* Quick search terms */}
                        {trending.terms?.length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <TrendingUp className="w-3.5 h-3.5 text-gold" />
                                    <span className="text-[10px] text-gold uppercase tracking-widest font-semibold">Popular Searches</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {trending.terms.map(term => (
                                        <button key={term} onClick={() => applyTerm(term)}
                                            className="px-3 py-1.5 rounded-full text-xs text-white/70 bg-white/[0.05] border border-white/[0.08] hover:border-gold/30 hover:text-white hover:bg-white/[0.08] transition-all duration-200"
                                            data-testid={`trending-term-${term.toLowerCase().replace(/\s+/g, '-')}`}>
                                            <Search className="w-2.5 h-2.5 inline mr-1.5 opacity-40" />{term}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Top destinations */}
                        {trending.cities?.length > 0 && (
                            <div className="mb-3">
                                <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-3.5 h-3.5 text-gold" />
                                    <span className="text-[10px] text-gold uppercase tracking-widest font-semibold">Top Destinations</span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {trending.cities.slice(0, 6).map(city => (
                                        <button key={city.id} onClick={() => go(`/city/${city.name.toLowerCase().replace(/\s+/g, '-')}`)}
                                            className="group flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/[0.05] transition-colors text-left"
                                            data-testid={`trending-city-${city.id}`}>
                                            <div className="w-8 h-8 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                                {city.image_url
                                                    ? <img src={city.image_url} alt={city.name} className="w-full h-full object-cover" />
                                                    : <div className="w-full h-full flex items-center justify-center"><MapPin className="w-3 h-3 text-gold/40" /></div>
                                                }
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-medium text-white truncate group-hover:text-gold transition-colors">{city.name}</p>
                                                <p className="text-[10px] text-white/30">{city.country}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Featured experiences */}
                        {trending.listings?.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Ticket className="w-3.5 h-3.5 text-amber-400" />
                                    <span className="text-[10px] text-amber-400 uppercase tracking-widest font-semibold">Featured Experiences</span>
                                </div>
                                {trending.listings.slice(0, 3).map(listing => (
                                    <button key={listing.id} onClick={() => go(`/experience/${listing.id}`)}
                                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors text-left"
                                        data-testid={`trending-listing-${listing.id}`}>
                                        <div className="w-9 h-9 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                            {listing.featured_image
                                                ? <img src={getImageUrl(listing.featured_image)} alt={listing.title} className="w-full h-full object-cover" />
                                                : <div className="w-full h-full flex items-center justify-center"><Ticket className="w-3.5 h-3.5 text-amber-400/40" /></div>
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate">{listing.title}</p>
                                            {listing.from_price > 0 && <p className="text-[10px] text-gold">from {formatCurrency(listing.from_price, listing.currency?.toUpperCase() || 'EUR')}</p>}
                                        </div>
                                        <ArrowRight className="w-3 h-3 text-white/20 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
